import type { ActivationStateResponse } from '../../../queries/bizy';
import type { BizyPageType } from '../store/use-bizy-page-context-store';

export type AudienceSegment = 'trial_admin' | 'trial_non_admin' | 'active' | 'canceled';

export function getAudienceSegment(companyStatus: string, isAdmin: boolean): AudienceSegment {
	if (companyStatus === 'non_renewing') return 'canceled';
	if (companyStatus === 'in_trial') return isAdmin ? 'trial_admin' : 'trial_non_admin';
	return 'active';
}

interface GraduatingChip {
	key: string;
	pages: BizyPageType[];
	audience: ('trial_admin' | 'trial_non_admin')[];
	retireWhen: (state: ActivationStateResponse) => boolean;
}

const graduatingChips: GraduatingChip[] = [
	{
		key: 'trialPrompts.home.getStarted',
		pages: ['home'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever || s.user.has_active_one_on_one,
	},
	{
		key: 'trialPrompts.home.firstRecognition',
		pages: ['home'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.home.inviteTeam',
		pages: ['home'],
		audience: ['trial_admin'],
		retireWhen: (s) => (s.admin?.company_user_count ?? 0) >= 2,
	},
	{
		key: 'trialPrompts.team.setUpTeam',
		pages: ['team'],
		audience: ['trial_admin'],
		retireWhen: (s) => (s.admin?.company_user_count ?? 0) >= 2,
	},
	{
		key: 'trialPrompts.team.invitePeople',
		pages: ['team'],
		audience: ['trial_admin'],
		retireWhen: (s) => (s.admin?.company_user_count ?? 0) >= 2,
	},
	{
		key: 'trialPrompts.team.whatCanIDo',
		pages: ['team'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.1on1.howMeetingsWork',
		pages: ['1on1_list', '1on1'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.has_active_one_on_one,
	},
	{
		key: 'trialPrompts.1on1.setUpFirst',
		pages: ['1on1_list', '1on1'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.has_active_one_on_one,
	},
	{
		key: 'trialPrompts.1on1.bizyInMeetings',
		pages: ['1on1_list', '1on1'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.has_completed_one_on_one,
	},
	{
		key: 'trialPrompts.goals.howGoalsWork',
		pages: ['goals'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.goals.writeFirst',
		pages: ['goals'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.goals.shareGoal',
		pages: ['goals'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.recaps.whatAreRecaps',
		pages: ['recaps'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.recaps.howToSetUp',
		pages: ['recaps'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.recaps.performanceReviews',
		pages: ['recaps'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.advice.howAdviceWorks',
		pages: ['advice'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.advice.askForAdvice',
		pages: ['advice'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.advice.adviceVsRecognition',
		pages: ['advice'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.profile.howProfileWorks',
		pages: ['profile_self', 'profile_other'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.profile.whatShowsUp',
		pages: ['profile_self', 'profile_other'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
	{
		key: 'trialPrompts.profile.pointsAndValues',
		pages: ['profile_self', 'profile_other'],
		audience: ['trial_admin', 'trial_non_admin'],
		retireWhen: (s) => s.user.recognition_given_ever,
	},
];

const normalPromptKeysByPage: Partial<Record<BizyPageType, string[]>> = {
	home: [
		'suggestedPrompts.home.whatsGoingOn',
		'suggestedPrompts.home.whoToRecognize',
		'suggestedPrompts.home.upcomingCelebration',
		'suggestedPrompts.home.teamValues',
		'suggestedPrompts.home.notRecognized',
		'suggestedPrompts.home.helpRecognize',
	],

	'1on1_list': [
		'suggestedPrompts.1on1_list.needsAgenda',
		'suggestedPrompts.1on1_list.notMetRecently',
		'suggestedPrompts.1on1_list.focusThisWeek',
		'suggestedPrompts.1on1_list.openActionItems',
		'suggestedPrompts.1on1_list.prioritize',
		'suggestedPrompts.1on1_list.catchUp',
	],

	'1on1': [
		'suggestedPrompts.1on1.talkingPoints',
		'suggestedPrompts.1on1.actionItems',
		'suggestedPrompts.1on1.recognizedFor',
		'suggestedPrompts.1on1.lastMeeting',
		'suggestedPrompts.1on1.checkinTrend',
		'suggestedPrompts.1on1.onPlate',
	],

	goals: [
		'suggestedPrompts.goals.overdue',
		'suggestedPrompts.goals.comingDue',
		'suggestedPrompts.goals.writeGoal',
		'suggestedPrompts.goals.structureGoal',
		'suggestedPrompts.goals.focusing',
	],

	recaps: ['suggestedPrompts.recaps.whatAreRecaps', 'suggestedPrompts.recaps.howToUse', 'suggestedPrompts.recaps.howToSetUp'],

	rewards: [
		'suggestedPrompts.rewards.howRewardsWork',
		'suggestedPrompts.rewards.typesAvailable',
		'suggestedPrompts.rewards.howToRedeem',
		'suggestedPrompts.rewards.redemptionHistory',
	],

	team: [
		'suggestedPrompts.team.coachingSupport',
		'suggestedPrompts.team.checkinsTrending',
		'suggestedPrompts.team.notRecognized',
		'suggestedPrompts.team.discussThisWeek',
		'suggestedPrompts.team.aboveAndBeyond',
		'suggestedPrompts.team.teamValues',
		'suggestedPrompts.team.upcomingMilestones',
		'suggestedPrompts.team.teamSummary',
	],

	incentives: [
		'suggestedPrompts.incentives.howIncentivesWork',
		'suggestedPrompts.incentives.howToParticipate',
		'suggestedPrompts.incentives.howToKnowCompleted',
	],

	advice: [
		'suggestedPrompts.advice.summarize',
		'suggestedPrompts.advice.patterns',
		'suggestedPrompts.advice.writeAdvice',
		'suggestedPrompts.advice.improve',
		'suggestedPrompts.advice.whatToGive',
		'suggestedPrompts.advice.respond',
	],

	profile_self: [
		'suggestedPrompts.profile_self.recognizedFor',
		'suggestedPrompts.profile_self.recentlyRecognized',
		'suggestedPrompts.profile_self.recognizesMeOften',
		'suggestedPrompts.profile_self.activityOverTime',
		'suggestedPrompts.profile_self.highlights',
		'suggestedPrompts.profile_self.knownFor',
	],

	profile_other: [
		'suggestedPrompts.profile_other.recentRecognition',
		'suggestedPrompts.profile_other.writeRecognition',
		'suggestedPrompts.profile_other.values',
		'suggestedPrompts.profile_other.lastRecognized',
		'suggestedPrompts.profile_other.currentGoals',
		'suggestedPrompts.profile_other.prepareOneOnOne',
		'suggestedPrompts.profile_other.relationship',
	],

	profile_bot: [],

	bookmarks: ['suggestedPrompts.bookmarks.whatAreBookmarks', 'suggestedPrompts.bookmarks.howToUse', 'suggestedPrompts.bookmarks.addNotes'],

	settings: [
		'suggestedPrompts.settings.connectSlackTeams',
		'suggestedPrompts.settings.notificationSettings',
		'suggestedPrompts.settings.updateProfile',
		'suggestedPrompts.settings.rewardPreferences',
		'suggestedPrompts.settings.integrations',
	],

	admin: ['suggestedPrompts.admin.participationTrends', 'suggestedPrompts.admin.lowestEngagement', 'suggestedPrompts.admin.improveRates'],
};

function shuffleArray<T>(arr: T[]): T[] {
	const shuffled = [...arr];
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
	}
	return shuffled;
}

export function getNormalPromptKeys(pageType: BizyPageType, count = 3): string[] {
	const pool = normalPromptKeysByPage[pageType] ?? normalPromptKeysByPage.home ?? [];
	return shuffleArray(pool).slice(0, count);
}

function getGraduatingChipsForPage(pageType: BizyPageType, segment: 'trial_admin' | 'trial_non_admin', activationState: ActivationStateResponse): string[] {
	return graduatingChips
		.filter((chip) => chip.pages.includes(pageType) && chip.audience.includes(segment) && !chip.retireWhen(activationState))
		.map((chip) => chip.key);
}

const canceledAdminPromptKeys: string[] = ['canceledPrompts.reopenAccount', 'canceledPrompts.contactSupport'];
const canceledNonAdminPromptKeys: string[] = ['canceledPrompts.whoAreAdmins'];

export function getCanceledPromptKeys(isAdmin: boolean): string[] {
	return isAdmin ? canceledAdminPromptKeys : canceledNonAdminPromptKeys;
}

export function getPromptChips(
	pageType: BizyPageType,
	segment: AudienceSegment,
	activationState: ActivationStateResponse | undefined,
	isAdmin: boolean,
	count = 3
): string[] {
	if (segment === 'canceled') {
		return getCanceledPromptKeys(isAdmin);
	}

	if ((segment === 'trial_admin' || segment === 'trial_non_admin') && activationState) {
		const active = getGraduatingChipsForPage(pageType, segment, activationState);
		if (active.length > 0) {
			return shuffleArray(active).slice(0, count);
		}
	}

	return getNormalPromptKeys(pageType, count);
}

// Legacy exports kept for backward compatibility with existing tests
export function getRandomPromptKeys(pageType: BizyPageType, count = 3): string[] {
	return getNormalPromptKeys(pageType, count);
}

export function getTrialPromptKeys(pageType: BizyPageType, count = 3): string[] | null {
	const trialPages: BizyPageType[] = ['home', 'team', '1on1_list', '1on1', 'goals', 'recaps', 'advice', 'profile_self', 'profile_other', 'profile_bot'];
	if (!trialPages.includes(pageType)) return null;
	if (pageType === 'profile_bot') return [];

	const keys = graduatingChips.filter((chip) => chip.pages.includes(pageType)).map((chip) => chip.key);

	return shuffleArray(keys).slice(0, count);
}
